import { createContext, useContext, useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

const AuthContext = createContext(null);

// Remove trailing slash if present to avoid double slashes
const API_BASE = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") || "";
const API = `${API_BASE}/api`;

export function AuthProvider({ children }) {
  const [token, setToken] = useState(localStorage.getItem("pasal_token"));
  const [shopName, setShopName] = useState(
    localStorage.getItem("shop_name") || "",
  );
  const [shopNameEn, setShopNameEn] = useState(
    localStorage.getItem("shop_name_en") || "",
  );
  const [userName, setUserName] = useState(
    localStorage.getItem("user_name") || "",
  );
  const [userRole, setUserRole] = useState(
    localStorage.getItem("user_role") || "",
  );
  // Three states, and the difference matters:
  //   true  - the server says this shop is set up
  //   false - the server says it is NOT, so offer to create it
  //   null  - we could not ask
  const [isSetup, setIsSetup] = useState(null);
  // Set when the check could not be made at all, as opposed to answered.
  const [setupCheckFailed, setSetupCheckFailed] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check if shop is set up
  useEffect(() => {
    checkSetup();
  }, []);

  const checkSetup = async () => {
    setSetupCheckFailed(false);
    try {
      const res = await axios.get(`${API}/auth/check`);
      setIsSetup(res.data.is_setup);
      setSetupCheckFailed(false);
      if (res.data.shop_name) {
        setShopName(res.data.shop_name);
      }
    } catch (err) {
      // An unreachable server is NOT an unconfigured shop. This used to set
      // isSetup to false, so anyone whose connection was down -- on a VPN, on
      // another network, or with the backend simply stopped -- was shown the
      // "create your shop" form for a shop that already existed. Leave it
      // unknown and let the screen say it could not reach the server.
      console.error("Setup check failed:", err);
      setIsSetup(null);
      setSetupCheckFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const setupShop = async (pin, shopNameNp, shopNameEnglish) => {
    const res = await axios.post(`${API}/auth/setup`, {
      pin,
      shop_name: shopNameNp,
      shop_name_en: shopNameEnglish,
    });

    const { access_token, shop_name, shop_name_en, user_name, user_role } = res.data;
    localStorage.setItem("pasal_token", access_token);
    localStorage.setItem("shop_name", shop_name);
    localStorage.setItem("shop_name_en", shop_name_en);
    localStorage.setItem("user_name", user_name);
    localStorage.setItem("user_role", user_role);
    setToken(access_token);
    setShopName(shop_name);
    setShopNameEn(shop_name_en);
    setUserName(user_name);
    setUserRole(user_role);
    setIsSetup(true);
    return res.data;
  };

  const loginWithUser = async (userId, pin) => {
    const res = await axios.post(`${API}/auth/login`, { user_id: userId, pin });

    const { access_token, shop_name, shop_name_en, user_name, user_role } = res.data;
    localStorage.setItem("pasal_token", access_token);
    localStorage.setItem("shop_name", shop_name);
    localStorage.setItem("shop_name_en", shop_name_en);
    localStorage.setItem("user_name", user_name);
    localStorage.setItem("user_role", user_role);
    setToken(access_token);
    setShopName(shop_name);
    setShopNameEn(shop_name_en);
    setUserName(user_name);
    setUserRole(user_role);
    return res.data;
  };

  // A dead session should say so, wherever it is noticed.
  //
  // Without this, an expired or revoked token surfaced as whatever the server
  // happened to say, on whatever screen the shopkeeper was on -- "User not
  // found" in the middle of a checkout, with no way to understand it and no way
  // out. Tokens last 30 days, so every till would eventually hit exactly that,
  // mid-sale.
  //
  // Held in a ref so the interceptor is registered once rather than on every
  // token change, and so it always sees the current token.
  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        const status = err.response?.status;
        const url = err.config?.url || "";
        // Login and setup answer 401 for a wrong PIN, which is not a dead
        // session -- those screens report it themselves.
        const isAuthCall = /\/auth\/(login|setup|check|users)/.test(url);

        if (status === 401 && !isAuthCall && tokenRef.current) {
          logout();
          toast.error("Please log in again / फेरि लगइन गर्नुहोस्");
        }
        return Promise.reject(err);
      },
    );
    return () => axios.interceptors.response.eject(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    localStorage.removeItem("pasal_token");
    localStorage.removeItem("shop_name");
    localStorage.removeItem("shop_name_en");
    localStorage.removeItem("user_name");
    localStorage.removeItem("user_role");
    setToken(null);
    setShopName("");
    setShopNameEn("");
    setUserName("");
    setUserRole("");
  };

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${token}` },
  });

  const value = {
    token,
    shopName,
    shopNameEn,
    userName,
    userRole,
    isSetup,
    setupCheckFailed,
    loading,
    isAuthenticated: !!token,
    setupShop,
    loginWithUser,
    logout,
    getAuthHeader,
    checkSetup,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
